import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, LogOut, User, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

/* Vaste onderbalk voor telefoon/tablet: 4 rol-specifieke hoofdtabs + "Meer" (bottom sheet met alle schermen per groep) */
const PRIMARY_BY_ROLE = {
  owner: ["/repairs", "/calendar", "/invoices", "/dashboard"],
  mechanic: ["/repairs", "/labor-times", "/hours", "/workboard"],
  reception: ["/calendar", "/repairs", "/customers", "/quotes"],
  warehouse: ["/inventory", "/purchase-orders", "/delivery-scan", "/repairs"],
  admin: ["/invoices", "/bank", "/customers", "/repairs"],
};
const roleOf = (user) => {
  if (!user) return "owner";
  if (user.role === "owner" || user.role === "super_admin") return "owner";
  const p = new Set(user.permissions || []);
  if (p.has("inventory.view") && !p.has("invoices.view") && !p.has("calendar.view")) return "warehouse";
  if (p.has("invoices.view") && !p.has("repairs.edit")) return "admin";
  if (p.has("calendar.view") && p.has("customers.view")) return "reception";
  return "mechanic";
};

export const MobileBottomNav = ({ nav, groups }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const loc = useLocation();
  const [more, setMore] = useState(false);
  const wanted = PRIMARY_BY_ROLE[roleOf(user)];
  const primary = [...wanted.map((p) => nav.find((n) => n.to === p)).filter(Boolean), ...nav.filter((n) => !wanted.includes(n.to))].slice(0, 4);
  const primaryActive = primary.some((n) => loc.pathname.startsWith(n.to));

  const Item = ({ n }) => (
    <NavLink to={n.to} onClick={() => setMore(false)} className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 min-h-[60px] text-[10px] leading-none transition-colors ${isActive ? "text-primary font-bold" : "text-muted-foreground"}`} data-testid={`mnav-${n.testId}`}>
      {({ isActive }) => (<>
        <span className={`absolute top-0 h-[3px] w-10 rounded-b-full bg-primary transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
        <span className={`grid place-items-center h-7 w-12 rounded-full transition-colors ${isActive ? "bg-primary/12" : ""}`}><n.icon className="h-5 w-5" /></span>
        <span className="truncate max-w-[76px]">{t(n.key)}</span>
      </>)}
    </NavLink>
  );

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-card/95 backdrop-blur grid grid-cols-5 shadow-[0_-6px_20px_-12px_rgba(0,0,0,.25)]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        {primary.map((n) => <Item key={n.to} n={n} />)}
        <button type="button" onClick={() => setMore(true)} className={`relative flex flex-col items-center justify-center gap-1 min-h-[60px] text-[10px] leading-none ${!primaryActive ? "text-primary font-bold" : "text-muted-foreground"}`} data-testid="mnav-more">
          <span className={`absolute top-0 h-[3px] w-10 rounded-b-full bg-primary ${!primaryActive ? "opacity-100" : "opacity-0"}`} />
          <span className={`grid place-items-center h-7 w-12 rounded-full ${!primaryActive ? "bg-primary/12" : ""}`}><Menu className="h-5 w-5" /></span>
          <span>{t("more")}</span>
        </button>
      </nav>
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[85dvh] overflow-y-auto lg:hidden [&>button]:hidden" data-testid="mnav-more-sheet">
          <SheetTitle className="sr-only">{t("more")}</SheetTitle>
          <div className="sticky top-0 bg-background/95 backdrop-blur px-4 pt-3 pb-2 border-b border-border flex items-center justify-between">
            <div className="mx-auto absolute left-1/2 -translate-x-1/2 top-1.5 h-1 w-10 rounded-full bg-border" />
            <div className="pt-2"><div className="font-display font-bold">{user?.name}</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{user?.role}</div></div>
            <button onClick={() => setMore(false)} className="h-9 w-9 grid place-items-center rounded-full border border-border" data-testid="mnav-more-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="p-4 space-y-5 pb-8">
            {groups.map((g) => {
              const items = nav.filter((n) => n.group === g);
              if (!items.length) return null;
              return (
                <div key={g}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-primary mb-2">{t(g)}</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {items.map((n) => (
                      <NavLink key={n.to} to={n.to} onClick={() => setMore(false)} className={({ isActive }) => `flex flex-col items-center gap-1.5 rounded-xl border p-2.5 text-center text-[11px] leading-tight min-h-[76px] justify-center ${isActive ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border text-foreground"}`} data-testid={`mnav-sheet-${n.testId}`}>
                        <n.icon className="h-5 w-5 shrink-0" /><span className="w-full max-w-full line-clamp-3 break-words hyphens-auto text-[10.5px]" lang="nl">{t(n.key)}</span>
                      </NavLink>
                    ))}
                  </div>
                </div>);
            })}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
              <NavLink to="/my-profile" onClick={() => setMore(false)} className="flex items-center justify-center gap-2 h-11 rounded-full border border-border text-sm" data-testid="mnav-profile"><User className="h-4 w-4" />{t("myProfile")}</NavLink>
              <button onClick={logout} className="flex items-center justify-center gap-2 h-11 rounded-full border border-border text-sm text-rose-600" data-testid="mnav-logout"><LogOut className="h-4 w-4" />{t("logout")}</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
